import React, { useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { NotFoundPage } from './NotFoundPage';
import { motion } from 'framer-motion';
import { ArrowRightIcon, ChevronRightIcon, MapPinIcon, CarIcon } from 'lucide-react';
import { Header } from '../components/Header';
import { Footer } from '../components/Footer';
import { SEO } from '../components/SEO';
import { RegioMap } from '../components/RegioMap';
import { OfficeAddressBlock } from '../components/OfficeAddressBlock';
import { CaseWebsiteLink } from '../components/CaseWebsiteLink';
import { werkgebiedPlaces, getWerkgebiedPlace } from '../data/werkgebied';
import { getClientCaseBySlug } from '../data/allClientCases';
import { buildBreadcrumbList, buildFAQSchema, buildServiceSchema } from '../lib/structuredData';
const EASE = [0.23, 1, 0.32, 1];
export const WerkgebiedPage: React.FC = () => {
  const {
    plaats
  } = useParams<{
    plaats: string;
  }>();
  const place = getWerkgebiedPlace(plaats || '');
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [plaats]);

  // Onbekende plaats: toon de echte 404 in plaats van stil door te sturen.
  if (!place) {
    return <NotFoundPage data-id="element-3459" data-mp="2cm0zi3ajn0ir" />;
  }
  const path = `/werkgebied/${place.slug}`;
  const nearbyCases = place.cases.map(c => ({
    ...c,
    data: getClientCaseBySlug(c.slug)
  })).filter((c): c is typeof c & {
    data: NonNullable<typeof c.data>;
  } => Boolean(c.data));
  const otherPlaces = werkgebiedPlaces.filter(p => p.slug !== place.slug);
  return <div className="w-full min-h-screen bg-white text-[#111827] font-sans" data-id="element-3460" data-mp="2vdtwe8sqh5vr">
      <SEO title={`Online marketing en SEO in ${place.name} | Noord Digital`} description={place.metaDescription} keywords={`online marketing ${place.shortName.toLowerCase()}, seo ${place.shortName.toLowerCase()}, online zichtbaarheid ${place.shortName.toLowerCase()}, webapplicatie laten maken ${place.shortName.toLowerCase()}, lokale seo ${place.municipality.toLowerCase()}`} canonical={`https://noorddigital.nl${path}`} jsonLd={[buildServiceSchema({
      name: `Online marketing en SEO in ${place.name}`,
      description: place.metaDescription,
      path,
      serviceType: 'Online marketing, SEO en maatwerk webapplicaties',
      city: place.name
    }), buildBreadcrumbList([{
      name: 'Werkgebied',
      path: '/werkgebied'
    }, {
      name: place.name,
      path
    }]), buildFAQSchema(place.faq)]} data-id="element-3461" data-mp="30dpy21dykl85" />
      <Header data-id="element-3462" data-mp="2waa7p2iczn5h" />

      <main data-id="element-3463" data-mp="2w9r0afqc4h5p">
        {/* Hero */}
        <section className="w-full bg-[#1B2B44] pt-32 pb-20 md:pb-24" data-id="element-3464" data-mp="2w8z1yx5cs5bq">
          <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-[1200px]" data-id="element-3465" data-mp="2rbfq23w4o4my">
            <nav aria-label="Kruimelpad" className="mb-8" data-id="element-3466" data-mp="35ztrqbht5n1x">
              <ol className="flex flex-wrap items-center gap-1.5 text-sm text-white/70" data-id="element-3467" data-mp="311s6pl0tndnt">
                <li data-id="element-3468" data-mp="3144pg0eels1x">
                  <Link to="/" className="hover:text-white transition-colors duration-200" data-id="element-3469" data-mp="3f19z0adbt91x">
                    Home
                  </Link>
                </li>
                <li aria-hidden="true" data-id="element-3470" data-mp="3a0dnbamqgily">
                  <ChevronRightIcon className="h-3.5 w-3.5 text-white/40" data-id="element-3471" data-mp="3tnqn6or3soz8" />
                </li>
                <li data-id="element-3472" data-mp="2ih2nclvp2x0">
                  <Link to="/werkgebied" className="hover:text-white transition-colors duration-200" data-id="element-3473" data-mp="2gw9nrf7ps19">
                    Werkgebied
                  </Link>
                </li>
                <li aria-hidden="true" data-id="element-3474" data-mp="3oomcftoj6j6s">
                  <ChevronRightIcon className="h-3.5 w-3.5 text-white/40" data-id="element-3475" data-mp="3onkqgu5z8caa" />
                </li>
                <li className="font-medium text-white" aria-current="page" data-id="element-3476" data-mp="3aw66ol48w726">
                  {place.name}
                </li>
              </ol>
            </nav>

            <motion.div className="max-w-3xl" initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.3,
            ease: EASE
          }} data-id="element-3477" data-mp="31033dbxnq2dn">
              <p className="inline-flex items-center gap-2 text-sm font-medium text-white/80 mb-5" data-id="element-3478" data-mp="2s0zjw6ukvkym">
                <MapPinIcon className="h-4 w-4" aria-hidden="true" data-id="element-3479" data-mp="2x02d8u1742qr" />
                {place.municipality}
              </p>
              <h1 className="font-bold text-white leading-tight mb-6 text-4xl md:text-5xl lg:text-6xl" data-id="element-3480" data-mp="2jygu8lbduewb">
                Online marketing en SEO in {place.name}
              </h1>
              <p className="text-white/90 text-lg md:text-xl leading-relaxed mb-8" data-id="element-3481" data-mp="2f546edej2c4m">
                {place.intro}
              </p>
              <div className="flex flex-col sm:flex-row gap-4" data-id="element-3482" data-mp="267bqb70zf0g6">
                <a href="/#contact" className="inline-flex items-center justify-center py-4 px-8 bg-white text-[#004AC7] font-semibold rounded-lg shadow-md hover:bg-gray-100 transition-colors duration-200 group" data-id="element-3483" data-mp="264plvg9e1126">
                  Plan een kennismaking
                  <ArrowRightIcon className="h-5 w-5 ml-2 transition-transform duration-200 group-hover:translate-x-1" aria-hidden="true" data-id="element-3484" data-mp="1s82kn1aejoqg" />
                </a>
                <a href={`tel:+31653689686`} className="inline-flex items-center justify-center py-4 px-8 border-2 border-white text-white font-semibold rounded-lg hover:bg-white/10 transition-colors duration-200" data-id="element-3485" data-mp="3ml26hc5dlf66">
                  Bel 06 53689686
                </a>
              </div>
            </motion.div>
          </div>
        </section>

        {/* Ondernemersklimaat + bereikbaarheid */}
        <section className="w-full bg-white py-20 md:py-24" data-id="element-3486" data-mp="1m4o8b0xvh5uf">
          <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-[1200px]" data-id="element-3487" data-mp="1m4651kcslrd0">
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16" data-id="element-3488" data-mp="pfgj9iy1oelr">
              <div className="lg:col-span-7" data-id="element-3489" data-mp="ucq7wwdm0m8j">
                <h2 className="font-bold text-[#111827] text-3xl md:text-4xl mb-6" data-id="element-3490" data-mp="udrt3tocap3z">
                  Ondernemen in {place.name}
                </h2>
                {place.klimaat.map(paragraph => <p key={paragraph.slice(0, 32)} className="text-[#4B5563] text-lg leading-relaxed mb-5 last:mb-0" data-id="element-3491" data-mp="13freszaauhd9">
                    {paragraph}
                  </p>)}

                <div className="mt-10 flex gap-4 border-t border-gray-200 pt-8" data-id="element-3492" data-mp="2esk1vws5ahmz">
                  <div className="flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-xl bg-[#F7F8FA] text-[#004AC7]" data-id="element-3493" data-mp="equ213ombz7i">
                    <CarIcon className="h-5 w-5" aria-hidden="true" data-id="element-3494" data-mp="aoqkpyf9q0eb" />
                  </div>
                  <div data-id="element-3495" data-mp="oo8g4wn2kbwn">
                    <h3 className="font-semibold text-[#111827] text-lg mb-1" data-id="element-3496" data-mp="ooi64k846j2c">
                      Vanaf Oudkarspel: {place.bereikbaarheid.afstand}, {place.bereikbaarheid.reistijd}
                    </h3>
                    <p className="text-[#6B7280] leading-relaxed" data-id="element-3497" data-mp="3s3xdfdp3yejs">
                      {place.bereikbaarheid.tekst}
                    </p>
                  </div>
                </div>
              </div>

              <aside className="lg:col-span-5" data-id="element-3498" data-mp="5si8otwdanbd">
                <div className="rounded-2xl border border-gray-200 bg-[#F7F8FA] p-6" data-id="element-3499" data-mp="zfimvqstvzx">
                  <RegioMap activeSlug={place.slug} data-id="element-3500" data-mp="39ez2j45ky22b" />
                  <p className="mt-4 text-xs text-[#6B7280]" data-id="element-3501" data-mp="39dfnfgvntx24">
                    Schematisch overzicht, afstanden zijn benaderingen.
                  </p>
                </div>
              </aside>
            </div>
          </div>
        </section>

        {/* Diensten */}
        <section className="w-full bg-[#F7F8FA] py-20 md:py-24" data-id="element-3502" data-mp="1opvctvjace6j">
          <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-[1200px]" data-id="element-3503" data-mp="1ay8g5ulfl3n8">
            <h2 className="font-bold text-[#111827] text-3xl md:text-4xl mb-4 max-w-2xl" data-id="element-3504" data-mp="1eza8drgxkffp">
              Wat we in {place.name} voor je doen
            </h2>
            <p className="text-[#6B7280] text-lg leading-relaxed mb-12 max-w-2xl" data-id="element-3505" data-mp="22pe7pmn35t0w">
              We leveren overal in de regio dezelfde diensten. Dit zijn de
              onderdelen die in {place.name} het vaakst het verschil maken.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 items-stretch" data-id="element-3506" data-mp="1yn169afdgh9x">
              {place.diensten.map(dienst => <Link key={dienst.title} to={dienst.link} className="group flex flex-col rounded-2xl border border-gray-200 bg-white p-7 hover:border-[#004AC7] transition-colors duration-200" data-id="element-3507" data-mp="i8hxr9crnflb">
                  <h3 className="font-bold text-[#111827] text-xl mb-3" data-id="element-3508" data-mp="1dypicb9mld4v">{dienst.title}</h3>
                  <p className="text-[#6B7280] leading-relaxed mb-6 flex-grow" data-id="element-3509" data-mp="19wug94jvevq1">{dienst.body}</p>
                  <span className="mt-auto inline-flex items-center gap-1.5 text-sm font-semibold text-[#004AC7]" data-id="element-3510" data-mp="14x9hau4pandb">
                    Lees meer
                    <ArrowRightIcon className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-1" aria-hidden="true" data-id="element-3511" data-mp="zzzjor75r5ee" />
                  </span>
                </Link>)}
            </div>
          </div>
        </section>

        {/* Extra secties (alleen hoofdpagina) */}
        {place.extraSections && place.extraSections.length > 0 && <section className="w-full bg-white py-20 md:py-24" data-id="element-3512" data-mp="1ixajccwpc0ud">
            <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-[1200px]" data-id="element-3513" data-mp="1iwr8fg5gycpu">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-12 lg:gap-16" data-id="element-3514" data-mp="2gdg8p5ert6x4">
                {place.extraSections.map(section => <div key={section.title} data-id="element-3515" data-mp="2bkn4cin7s0lk">
                    <h2 className="font-bold text-[#111827] text-2xl md:text-3xl mb-4" data-id="element-3516" data-mp="2ixcolv1wh0am">
                      {section.title}
                    </h2>
                    <p className="text-[#4B5563] text-lg leading-relaxed" data-id="element-3517" data-mp="2nwfsvda232hj">{section.body}</p>
                  </div>)}
              </div>
            </div>
          </section>}

        {/* Klanten in de buurt */}
        <section className={`w-full py-20 md:py-24 ${place.extraSections ? 'bg-[#F7F8FA]' : 'bg-white'}`} data-id="element-3518" data-mp="2xvoftm6i7gue">
          <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-[1200px]" data-id="element-3519" data-mp="31tvzopw9ky9t">
            <h2 className="font-bold text-[#111827] text-3xl md:text-4xl mb-4" data-id="element-3520" data-mp="36t8m59j843bv">
              Klanten in de buurt
            </h2>
            {nearbyCases.length > 0 ? <>
                <p className="text-[#6B7280] text-lg leading-relaxed mb-10 max-w-2xl" data-id="element-3521" data-mp="3gjtkcvuzj5u9">
                  Werk dat we hebben opgeleverd voor ondernemers in de omgeving
                  van {place.name}.
                </p>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 items-stretch" data-id="element-3522" data-mp="3plt07ku9k3vm">
                  {nearbyCases.map(c => <article key={c.slug} className="flex flex-col overflow-hidden rounded-2xl border border-gray-200 bg-white" data-id="element-3523" data-mp="18bg7za9vumks">
                      <img src={c.data.heroImage} alt={`${c.data.clientName}, klantcase Noord Digital`} loading="lazy" className="h-48 w-full object-cover object-top" data-id="element-3524" data-mp="1iayspsxmacdw" />
                      <div className="flex flex-grow flex-col p-6" data-id="element-3525" data-mp="1n404vt6fvxts">
                        <p className="mb-2 flex items-center gap-1.5 text-sm text-[#6B7280]" data-id="element-3526" data-mp="1s0qq0ux3pjm2">
                          <MapPinIcon className="h-3.5 w-3.5 flex-shrink-0" aria-hidden="true" data-id="element-3527" data-mp="1w1aktru7cb51" />
                          {c.locatie}
                        </p>
                        <h3 className="font-bold text-[#111827] text-xl mb-3 min-h-[3.5rem]" data-id="element-3528" data-mp="261kwv94i3cim">{c.data.clientName}</h3>
                        <p className="text-[#6B7280] text-sm leading-relaxed mb-6 flex-grow line-clamp-4" data-id="element-3529" data-mp="6oyxsotp85u3">
                          {c.data.shortDescription}
                        </p>
                        <div className="mt-auto flex flex-wrap items-center justify-between gap-x-4 gap-y-2" data-id="element-3530" data-mp="43r73fepi4fz">
                          <Link to={`/cases/${c.slug}`} className="group/link inline-flex items-center gap-1.5 font-semibold text-[#004AC7] hover:text-[#003A9E] transition-colors duration-200 whitespace-nowrap" data-id="element-3531" data-mp="3vh5d93jmmiip">
                            Lees het verhaal
                            <ArrowRightIcon className="h-4 w-4 transition-transform duration-200 group-hover/link:translate-x-1" aria-hidden="true" data-id="element-3532" data-mp="n2d7st6b9tjd" />
                          </Link>
                          {c.data.website && <CaseWebsiteLink url={c.data.website} clientName={c.data.clientName} className="max-w-full" data-id="element-3533" data-mp="d477uh42exh5" />}
                        </div>
                      </div>
                    </article>)}
                </div>
              </> : <p className="text-[#6B7280] text-lg leading-relaxed max-w-2xl" data-id="element-3534" data-mp="2xuofxyaw84wy">
                Van {place.name} zelf staat nog geen case op onze site. Bekijk de{' '}
                <Link to="/werkgebied/alkmaar" className="font-semibold text-[#004AC7] hover:text-[#003A9E] transition-colors duration-200" data-id="element-3535" data-mp="1kvwd3gko27s8">
                  klanten rond Alkmaar
                </Link>{' '}
                of{' '}
                <a href="/#client-cases" className="font-semibold text-[#004AC7] hover:text-[#003A9E] transition-colors duration-200" data-id="element-3536" data-mp="1ayi1pvdlo96s">
                  alle klantcases
                </a>
                .
              </p>}
          </div>
        </section>

        {/* FAQ + adres */}
        <section className={`w-full py-20 md:py-24 ${place.extraSections ? 'bg-white' : 'bg-[#F7F8FA]'}`} data-id="element-3537" data-mp="15z5evamqb247">
          <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-[1200px]" data-id="element-3538" data-mp="s809drwojfly">
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16" data-id="element-3539" data-mp="x621t3fzwra3">
              <div className="lg:col-span-7" data-id="element-3540" data-mp="x3xt8875j43h">
                <h2 className="font-bold text-[#111827] text-3xl md:text-4xl mb-8" data-id="element-3541" data-mp="ia2n39ydrsvz">
                  Vragen over werken in {place.name}
                </h2>
                <div className="space-y-4" data-id="element-3542" data-mp="3mcevaal55b1">
                  {place.faq.map(f => <details key={f.question} className="group rounded-2xl border border-gray-200 bg-white p-6 [&_summary::-webkit-details-marker]:hidden" data-id="element-3543" data-mp="3mm4uxvmrcgq">
                      <summary className="flex cursor-pointer list-none items-center justify-between gap-4 font-semibold text-[#111827] hover:text-[#004AC7] transition-colors duration-200" data-id="element-3544" data-mp="dks4t9xvm8iy">
                        {f.question}
                        <ChevronRightIcon className="h-5 w-5 flex-shrink-0 text-[#004AC7] transition-transform duration-200 group-open:rotate-90" aria-hidden="true" data-id="element-3545" data-mp="mjvly8dhgdut" />
                      </summary>
                      <p className="mt-4 text-[#6B7280] leading-relaxed" data-id="element-3546" data-mp="w9oqipf7jspk">{f.answer}</p>
                    </details>)}
                </div>
              </div>
              <div className="lg:col-span-5" data-id="element-3547" data-mp="168xp5vl0pvi9">
                <OfficeAddressBlock travelNote={`Naar ${place.name}: ${place.bereikbaarheid.afstand}, ${place.bereikbaarheid.reistijd}.`} data-id="element-3548" data-mp="169p9ga9606t8" />
              </div>
            </div>
          </div>
        </section>

        {/* Andere plaatsen */}
        <section className="w-full bg-white py-16 md:py-20 border-t border-gray-100" data-id="element-3549" data-mp="2zrwlkgo31hvs">
          <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-[1200px]" data-id="element-3550" data-mp="2zpjvfg86817n">
            <h2 className="font-bold text-[#111827] text-2xl mb-6" data-id="element-3551" data-mp="34jem53m71gg8">Ook actief in</h2>
            <ul className="flex flex-wrap gap-3" data-id="element-3552" data-mp="39h6g036efcn2">
              {otherPlaces.map(p => <li key={p.slug} data-id="element-3553" data-mp="2bur035tmen1m">
                  <Link to={`/werkgebied/${p.slug}`} className="inline-flex items-center rounded-lg border border-gray-200 bg-[#F7F8FA] px-4 py-2 text-sm font-medium text-[#111827] hover:border-[#004AC7] hover:text-[#004AC7] transition-colors duration-200" data-id="element-3554" data-mp="2bvsad82tb5ia">
                    {p.name}
                  </Link>
                </li>)}
            </ul>
          </div>
        </section>

        {/* CTA */}
        <section className="w-full bg-[#004AC7] py-16 md:py-20" data-id="element-3555" data-mp="11mrfhgoaiec">
          <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-[1200px] flex flex-col lg:flex-row lg:items-center lg:justify-between gap-8" data-id="element-3556" data-mp="5zy0vr322qw1">
            <div className="max-w-2xl" data-id="element-3557" data-mp="1qkuqffzpgqed">
              <h2 className="font-bold text-white text-2xl md:text-3xl mb-3" data-id="element-3558" data-mp="1ljow5dqecv2q">
                Ondernemer in {place.name}? Laten we kennismaken.
              </h2>
              <p className="text-white/85 text-lg leading-relaxed" data-id="element-3559" data-mp="1gqvtqwiehytt">
                In een gesprek van een half uur kijken we naar je huidige
                online zichtbaarheid en vertellen we wat we zouden aanpakken. Je
                zit daarna tot niets verplicht.
              </p>
            </div>
            <a href="/#contact" className="inline-flex items-center justify-center gap-2 bg-white text-[#004AC7] font-semibold py-4 px-8 rounded-lg shadow-md hover:bg-gray-100 transition-colors duration-200 flex-shrink-0 whitespace-nowrap" data-id="element-3560" data-mp="xucquoy6x75p">
              Plan een kennismaking
              <ArrowRightIcon className="h-4 w-4" aria-hidden="true" data-id="element-3561" data-mp="2s6awkgxajkwu" />
            </a>
          </div>
        </section>
      </main>

      <Footer data-id="element-3562" data-mp="lkemv7ipjvda" />
    </div>;
};